import { emptySearchResults, emptySections, ephemerideContents, feedFilters, searchResultsTypes, sections } from '../../constants/newFeed'
import router from '../../router'
import { getKimpleFolder } from '../../services/KimpleFolderService'
import { getSubtopicByLocale } from '../../services/SubtopicService'
import { getPopularSuggestions, searchAvailableSuggestions } from '../../services/UserService'
import { isError } from '../../utils/api'
import { deepCopy } from '../../utils/object'

export default {
  state: {
    filters: {
      topicIds: [],
      skillIds: [],
      months: [],
      ODSIds: [],
      ageRangeIds: [],
      subjectIds: []
    },
    sections: deepCopy(emptySections),
    searchResults: deepCopy(emptySearchResults)
  },

  mutations: {
    hydrateFeedSection(state, { fromFeed, sectionName, data }) {
      if (Array.isArray(data)) {
        state.sections[sectionName].data = data
      } else {
        state.sections[sectionName].data = fromFeed
          ? state.sections[sectionName].data = data.data
          : [...state.sections[sectionName].data, ...data.data]
        state.sections[sectionName].totalCount = data.totalCount
        state.sections[sectionName].totalPages = data.pages
        state.sections[sectionName].page = data.pageNumber
      }
    },
    hydrateSearchResults(state, { type, firstLoading, data }) {
      if (firstLoading) {
        state.searchResults[type].data = [...data.data]
        state.searchResults[type].totalCount = data.totalCount
        state.searchResults[type].totalPages = data.pages
        state.searchResults[type].page = data.pageNumber
      } else {
        state.searchResults[type].data = [...state.searchResults[type].data, ...data.data]
        state.searchResults[type].totalCount = data.totalCount
        state.searchResults[type].totalPages = data.pages
        state.searchResults[type].page = data.pageNumber
      }
    },
    hydrateAvailableFilters(state, { data, filterName }) {
      state.filters[filterName] = data || []
    },
    hydrateEphemerideData(state, data) {
      state.searchResults.ephemeride.data = data
    },
    hydrateEphemerideContent(state, { content, firstLoading, data }) {
      if (firstLoading) {
        state.searchResults.ephemeride[content].data = [...data.data]
      } else {
        state.searchResults.ephemeride[content].data = [...state.searchResults.ephemeride[content].data, ...data.data]
      }
      state.searchResults.ephemeride[content].totalCount = data.totalCount
      state.searchResults.ephemeride[content].totalPages = data.pages
      state.searchResults.ephemeride[content].page = data.pageNumber
    },
    hydrateSubtopicData(state, data) {
      state.searchResults.subtopic.data = data
    },
    clearSearchResults(state) {
      state.searchResults = deepCopy(emptySearchResults)
    },
    clearSearchResultsContent(state) {
      const results = deepCopy(emptySearchResults)
      delete results.subtopic
      delete results.ephemeride
      state.searchResults = {
        ...state.searchResults,
        ...results
      }
    },
    clearFeedSections(state) {
      state.sections = deepCopy(emptySections)
    },
    setLoadingSection(state, { sectionName, loading }) {
      state.sections[sectionName].loading = loading
    },
    setLoadingSearchResult(state, { type, loading }) {
      state.searchResults[type].loading = loading
    },
    setLoadingEphemerideSection(state, { content, loading }) {
      state.searchResults.ephemeride[content].loading = loading
    }
  },

  actions: {
    async hydrateFeedSection({ commit }, { fromFeed, sectionName, isCustom, pageNumber, pageSize, limit }) {
      commit('setLoadingSection', { sectionName, loading: true })
      const section = sections.find(sec => sec.name === sectionName)
      let response
      if (isCustom) {
        response = await section
          .customService(pageNumber, pageSize, limit)
          .catch(e => e.response)
      } else {
        response = await section
          .service(pageNumber, pageSize, limit)
          .catch(e => e.response)
      }
      commit('setLoadingSection', { sectionName, loading: false })
      const success = !isError(response.status)
      if (success) {
        commit('hydrateFeedSection', {
          sectionName,
          fromFeed,
          data: Array.isArray(response.data)
            ? response.data
            : { ...response.data, pageNumber }
        })
      }
    },
    async searchResults({ commit }, { firstLoading = false, type, customLicense, pageNumber, pageSize, filters = {} }) {
      commit('setLoadingSearchResult', { type, loading: true })
      const section = searchResultsTypes.find(sec => sec.name === type)
      let response
      if (customLicense) {
        response = await section
          .customService(pageNumber, pageSize, filters)
          .catch(e => e.response)
      } else {
        response = await section
          .service(pageNumber, pageSize, filters)
          .catch(e => e.response)
      }
      commit('setLoadingSearchResult', { type, loading: false })
      const success = !isError(response.status)
      if (success) commit('hydrateSearchResults', { type, firstLoading, data: { ...response.data, pageNumber } })
    },
    async hydrateAvailableFilters({ commit }) {
      const values = await Promise.all(
        feedFilters.map(filter => filter.service({
          topicIds: router.currentRoute.query.topicIds,
          subtopicIds: router.currentRoute.query.subtopicIds,
          months: router.currentRoute.query.months,
          skillIds: router.currentRoute.query.skillIds,
          ODSIds: router.currentRoute.query.ODSIds,
          ageRangeIds: router.currentRoute.query.ageRangeIds,
          subjectIds: router.currentRoute.query.subjectIds,
          authorId: router.currentRoute.query.authorId
        }).catch(e => e.response))
      )
      values.forEach((response, i) => {
        const success = !isError(response.status)
        if (success) {
          commit('hydrateAvailableFilters', {
            data: response.data,
            filterName: feedFilters[i].name
          })
        }
      })
    },
    async hydrateEphemerideData({ commit }, { id, locale }) {
      const response = await getKimpleFolder(id, locale).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateEphemerideData', response.data)
      return { success, data: response.data }
    },
    async hydrateEphemerideContent({ commit }, { content, firstLoading, id, pageNumber, pageSize }) {
      commit('setLoadingEphemerideSection', { content, loading: true })
      const ephemerideContent = ephemerideContents.find(cont => cont.name === content)
      const response = await ephemerideContent.service({ id, pageNumber, pageSize }).catch(e => e.response)
      const success = !isError(response.status)
      commit('setLoadingEphemerideSection', { content, loading: false })
      if (success) commit('hydrateEphemerideContent', { content, firstLoading, data: response.data })
    },
    async hydrateSubtopicData({ commit }, id) {
      const response = await getSubtopicByLocale(id).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateSubtopicData', response.data)
      return { success, data: response.data }
    },
    async searchPopularContent({ commit }, data) {
      const response = await getPopularSuggestions(data).catch(e => e.response)
      const success = !isError(response.status)
      return { success, data: response.data }
    },
    async searchContentByText({ commit }, data) {
      const response = await searchAvailableSuggestions(data).catch(e => e.response)
      const success = !isError(response.status)
      return { success, data: response.data }
    },
    clearSearchResults({ commit }) {
      commit('clearSearchResults')
    },
    clearSearchResultsContent({ commit }) {
      commit('clearSearchResultsContent')
    },
    clearFeedSections({ commit }) {
      commit('clearFeedSections')
    }
  }
}
